/* redesign-pillar.js — behaviour for the Encyclopedia hub and its seven guides.

   This replaces SEVEN byte-identical copies of the same ~253-line inline
   <script>, one per pillar-*.liquid. The logic below is that script, with two
   deliberate changes:

     1. It finds its section generically (`.rd-pillar [data-section-id]`)
        rather than through `{{ section.id }}` interpolated into the source, so
        one cached file serves all eight pages.

     2. THE HEADER OFFSET NOW FOLLOWS THE REDESIGN, NOT THE OLD THEME. The
        inline version hardcoded 164px desktop / 90px mobile, which were the
        legacy header's heights. The redesign's header is --rd-header-h (76px)
        and the trust bar above it scrolls away, so an anchor jump using 164
        would have parked every heading ~76px too low and the sticky card
        would have floated a gap below the header. The value is read from the
        custom property with the same fallback redesign.css declares.

   Nothing here writes heading text, changes a heading's level, or reorders
   the document. The FAQ builder moves a button INTO an existing h4 and leaves
   the heading itself in place — the same shape redesign-recall.js uses. */

(function () {
  'use strict';

  var root = document.querySelector('.rd-pillar');
  if (!root) return;

  /* The redesign header's pinned height, plus a little breathing room. Read
     from the custom property so it cannot drift from the stylesheet. */
  function headerOffset() {
    var v = parseInt(
      getComputedStyle(document.documentElement).getPropertyValue('--rd-header-h'),
      10
    );
    if (!v || isNaN(v)) v = 76;
    return v + 12;
  }

  function isDesktop() {
    return window.matchMedia('(min-width: 901px)').matches;
  }

  /* ------------------------------------------------------- sticky sidebar */

  function initSticky(section) {
    var left = section.querySelector('[id^="olsLeft-"]');
    var rightWrap = section.querySelector('[id^="olsRightWrap-"]');
    var tocCard = section.querySelector('[id^="olsTocCard-"]');
    if (!left || !rightWrap || !tocCard) return;

    /* Reserves the card's height while it is position:fixed, so the right
       column does not collapse and take the page height with it. */
    var spacer = rightWrap.querySelector('.ols-toc-spacer');
    if (!spacer) {
      spacer = document.createElement('div');
      spacer.className = 'ols-toc-spacer';
      rightWrap.insertBefore(spacer, tocCard);
    }

    var raf = null;

    function measure() {
      rightWrap.style.position = 'relative';
      rightWrap.style.height = left.offsetHeight + 'px';
      var wrapRect = rightWrap.getBoundingClientRect();
      section.style.setProperty('--ols-card-width', wrapRect.width + 'px');
    }

    function resetToNormalFlow() {
      tocCard.classList.remove('is-fixed', 'is-stopped');
      tocCard.style.left = '';
      tocCard.style.top = '';
      tocCard.style.width = '';
      spacer.style.height = '0px';
    }

    function update() {
      if (!isDesktop()) {
        resetToNormalFlow();
        rightWrap.style.height = '';
        return;
      }

      measure();

      var stickyTop = headerOffset();
      var scrollY = window.pageYOffset || document.documentElement.scrollTop;
      var leftRect = left.getBoundingClientRect();
      var wrapRect = rightWrap.getBoundingClientRect();

      var startStick = scrollY + wrapRect.top - stickyTop;
      var stopStick = scrollY + leftRect.bottom - tocCard.offsetHeight - stickyTop;

      if (scrollY < startStick) { resetToNormalFlow(); return; }

      if (scrollY < stopStick) {
        tocCard.classList.add('is-fixed');
        tocCard.classList.remove('is-stopped');
        spacer.style.height = tocCard.offsetHeight + 'px';
        tocCard.style.width =
          getComputedStyle(section).getPropertyValue('--ols-card-width') || wrapRect.width + 'px';
        tocCard.style.left = wrapRect.left + 'px';
        tocCard.style.top = stickyTop + 'px';
        return;
      }

      /* Parked at the bottom of the sidebar column, so it never overlays the
         full-width section below. */
      tocCard.classList.remove('is-fixed');
      tocCard.classList.add('is-stopped');
      spacer.style.height = tocCard.offsetHeight + 'px';
      tocCard.style.left = '';
      tocCard.style.top = '';
      tocCard.style.width = '100%';
    }

    function onScroll() {
      if (raf) return;
      raf = requestAnimationFrame(function () { raf = null; update(); });
    }

    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', function () { update(); }, { passive: true });
    update();
  }

  /* ----------------------------------------------- anchor jumps with offset */

  function findTarget(id) {
    if (!id) return null;
    if (id.charAt(0) === '#') id = id.slice(1);
    return document.getElementById(id);
  }

  function scrollToId(id, pushState) {
    if (!id) return;
    if (id.charAt(0) === '#') id = id.slice(1);
    var target = findTarget(id);
    if (!target) return;

    var offset = headerOffset();

    function land() {
      var r = target.getBoundingClientRect();
      var y = window.pageYOffset || document.documentElement.scrollTop;
      window.scrollTo(0, Math.max(0, y + r.top - offset));
    }

    /* Re-corrected twice: images above the target can still be settling, and
       the browser's own hash jump lands late. */
    land();
    setTimeout(land, 80);
    setTimeout(land, 200);

    if (pushState) { try { history.pushState(null, '', '#' + id); } catch (e) {} }
  }

  function interceptClicks(section) {
    section.addEventListener('click', function (e) {
      var a = e.target && e.target.closest ? e.target.closest('a') : null;
      if (!a) return;

      var id = a.getAttribute('data-ols-anchor');
      if (!id) {
        var href = a.getAttribute('href') || '';
        if (href.charAt(0) === '#') id = href.slice(1);
      }
      if (!id || !findTarget(id)) return;

      e.preventDefault();
      e.stopPropagation();
      if (e.stopImmediatePropagation) e.stopImmediatePropagation();
      scrollToId(id, true);
    }, true);
  }

  function handleInitialHash() {
    if (window.location.hash && window.location.hash.length > 1) {
      var id = window.location.hash.slice(1);
      setTimeout(function () { scrollToId(id, false); }, 150);
      setTimeout(function () { scrollToId(id, false); }, 350);
    }
  }

  /* ------------------------------------------------ active TOC highlighting */

  function initActiveToc(section) {
    var links = section.querySelectorAll('a[data-ols-anchor]');
    if (!links.length) return;

    var map = {};
    Array.prototype.forEach.call(links, function (a) {
      var id = (a.getAttribute('data-ols-anchor') || '').trim();
      if (!id) return;
      if (!map[id]) map[id] = [];
      map[id].push(a);
    });

    var targets = Object.keys(map)
      .map(function (id) { return document.getElementById(id); })
      .filter(Boolean);
    if (!targets.length) return;

    function activate(id) {
      Array.prototype.forEach.call(links, function (a) { a.classList.remove('is-active'); });
      (map[id] || []).forEach(function (a) { a.classList.add('is-active'); });
    }

    function pick() {
      var line = headerOffset() + 6;
      var best = null;
      for (var i = 0; i < targets.length; i++) {
        if (targets[i].getBoundingClientRect().top <= line) best = targets[i];
      }
      if (best) activate(best.id);
    }

    window.addEventListener('scroll', function () { requestAnimationFrame(pick); }, { passive: true });
    window.addEventListener('resize', function () { requestAnimationFrame(pick); }, { passive: true });
    pick();
  }

  /* --------------------------------------------------- the hub's FAQ block

     REUSES THE SITE'S EXISTING ACCORDION — the one on the product pages, the
     FAQ page and the homepage — rather than the new style the handoff drew.
     That means the shared markup contract (.rd-faq__row / __btn / __n /
     __title / __mark / __body), the shared CSS in redesign.css, and the shared
     toggle in redesign.js. Nothing about the accordion is defined here.

     No click handler is attached below. redesign.js binds ONE delegated
     listener per `[data-rd-accordion]` container at load; the container is the
     page wrapper in templates/page.pillar-entry.liquid, which exists in the
     served HTML, so rows built here afterwards are picked up by delegation and
     get the shared open/close animation and single-open behaviour for free.

     THE HEADING IS NOT REPLACED. Each question stays the h4 it is on live, in
     place, with its own child nodes moved into the button inside it — so the
     outline Google reads is unchanged and any markup the admin typed inside a
     question survives. */

  function buildFaq(section) {
    /* HUB ONLY. The seven guides also carry a `Frequently Asked Questions`
       heading inside {{ page.content }}, but their Q&As are not headings at
       all, so there would be nothing to collapse. */
    if (!section.classList.contains('ols-pillar-hub')) return;

    var prose = section.querySelector('.ols-body .rte');
    if (!prose) return;

    var heads = prose.querySelectorAll('h2, h3, h4, h5, h6');
    var faqHead = null;
    for (var i = 0; i < heads.length; i++) {
      var t = (heads[i].textContent || '').trim();
      if (/frequently asked|^faq\b/i.test(t)) { faqHead = heads[i]; break; }
    }
    if (!faqHead) return;

    /* Questions are whatever level sits directly under the FAQ heading — h4
       today, read rather than assumed so a later edit still builds. */
    var qTag = null, scan = faqHead.nextElementSibling;
    while (scan) {
      if (/^H[2-6]$/.test(scan.tagName)) {
        if (scan.tagName <= faqHead.tagName) break;   /* back out to a sibling section */
        qTag = scan.tagName;
        break;
      }
      scan = scan.nextElementSibling;
    }
    if (!qTag) return;

    var items = [], node = faqHead.nextElementSibling, current = null;
    while (node) {
      var next = node.nextElementSibling;
      if (/^H[2-6]$/.test(node.tagName) && node.tagName < qTag) break;
      if (node.tagName === qTag) { current = { q: node, a: [] }; items.push(current); }
      else if (current) { current.a.push(node); }
      node = next;
    }
    if (!items.length) return;

    var list = document.createElement('div');
    list.className = 'rd-faq__list';
    faqHead.insertAdjacentElement('afterend', list);

    items.forEach(function (item, i) {
      var row = document.createElement('div');
      row.className = 'rd-faq__row';

      var panel = document.createElement('div');
      panel.className = 'rd-faq__body';
      panel.id = 'rd-faq-encyc-' + (i + 1);
      panel.hidden = true;

      var btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'rd-faq__btn';
      btn.setAttribute('aria-expanded', 'false');
      btn.setAttribute('aria-controls', panel.id);

      /* The zero is a pad, not a prefix — same as the PDP's, which went "010"
         once its list passed nine. */
      /* THE NUMBER AND THE +/- CARRY NO TEXT NODES. Everywhere else this
         component is used the row title is a plain <span> and the button sits
         beside it, so their characters are harmless. Here the button lives
         INSIDE an h4 that live already has, and any text inside that h4
         becomes part of the heading — `01What is ...?+-` instead of the
         question. The digits and the marks are therefore drawn from CSS
         (::before) in redesign-pillar.css, leaving the elements empty and the
         heading's text exactly the words live has indexed. */
      var n = document.createElement('span');
      n.className = 'rd-faq__n';
      n.setAttribute('aria-hidden', 'true');
      n.setAttribute('data-n', (i + 1 < 10 ? '0' : '') + (i + 1));

      var mid = document.createElement('span');
      var title = document.createElement('span');
      title.className = 'rd-faq__title';
      /* Moved, not copied from textContent — that would drop a <strong> or a
         link the admin typed inside the question. */
      while (item.q.firstChild) title.appendChild(item.q.firstChild);
      mid.appendChild(title);

      var mark = document.createElement('span');
      mark.className = 'rd-faq__mark';
      mark.setAttribute('aria-hidden', 'true');
      mark.innerHTML = '<span class="rd-faq__mark-plus"></span><span class="rd-faq__mark-minus"></span>';

      btn.appendChild(n); btn.appendChild(mid); btn.appendChild(mark);

      item.q.appendChild(btn);
      item.q.classList.add('rd-faq__q');

      row.appendChild(item.q);
      item.a.forEach(function (el) { panel.appendChild(el); });
      row.appendChild(panel);
      list.appendChild(row);
    });
  }

  /* --------------------------------------------------------------- init */

  var section = root.querySelector('[data-section-id]');
  if (!section) return;

  initSticky(section);
  interceptClicks(section);
  handleInitialHash();
  initActiveToc(section);
  buildFaq(section);
})();
